import { z } from '@faceup/validation'
import {
  DisclosureSubmissionAuditLogEmailType,
  DisclosureSubmissionAuditLogType,
  DisclosureSubmissionStatus,
} from '../enums'
import { memberRefSchema } from './investigationAuditLogSchema'

/**
 * What happened to a disclosure submission. The `type` is stored in its own column so the log can
 * be filtered on it, the remaining fields are the entry's payload.
 */
export const disclosureSubmissionAuditLogEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal(DisclosureSubmissionAuditLogType.InvitationSent) }),
  z.object({ type: z.literal(DisclosureSubmissionAuditLogType.ReminderSent) }),
  z.object({ type: z.literal(DisclosureSubmissionAuditLogType.VerificationCodeRequested) }),
  z.object({
    type: z.literal(DisclosureSubmissionAuditLogType.SubmissionReceived),
    // Whether the address had a pending invitation on the form, not which link was opened: the
    // invite link only prefills the address, so the answers are matched to the invite by it
    viaInvitation: z.boolean(),
  }),
  z.object({
    type: z.literal(DisclosureSubmissionAuditLogType.StatusChanged),
    previousStatus: z.enum(DisclosureSubmissionStatus),
    newStatus: z.enum(DisclosureSubmissionStatus),
    // Set only on a change by the auto-approval rules: whether they were applied again because
    // they changed, which the entry words differently (spec 13.1 §6)
    afterAutoApprovalRulesChange: z.boolean().optional(),
  }),
  z.object({
    type: z.literal(DisclosureSubmissionAuditLogType.AssigneeAdded),
    assignee: memberRefSchema,
  }),
  z.object({
    type: z.literal(DisclosureSubmissionAuditLogType.AssigneeRemoved),
    assignee: memberRefSchema,
  }),
  // Only that they changed: the text stays on the submission and never reaches the log (spec 13.1 §4.2)
  z.object({ type: z.literal(DisclosureSubmissionAuditLogType.NotesChanged) }),
  z.object({
    type: z.literal(DisclosureSubmissionAuditLogType.EmailDelivered),
    emailType: z.enum(DisclosureSubmissionAuditLogEmailType),
    deliveryEventId: z.string().min(1),
  }),
  z.object({
    type: z.literal(DisclosureSubmissionAuditLogType.EmailDeliveryFailed),
    emailType: z.enum(DisclosureSubmissionAuditLogEmailType),
    deliveryEventId: z.string().min(1),
  }),
])

export type DisclosureSubmissionAuditLogEvent = z.infer<
  typeof disclosureSubmissionAuditLogEventSchema
>

// Fails to compile when a type in the enum has no event above
;({}) satisfies Record<
  Exclude<DisclosureSubmissionAuditLogType, DisclosureSubmissionAuditLogEvent['type']>,
  never
>
